import type { FormattedExecutionResult, GraphQLFormattedError } from 'graphql'

type ExecutionResult = FormattedExecutionResult<unknown, unknown>
type PathError = GraphQLFormattedError & Required<Pick<GraphQLFormattedError, 'path'>>

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function asArray(value: unknown): readonly unknown[] {
  return Array.isArray(value) ? value : []
}

// "@" replaces a lost list index, so the path can't match a field.
function isPathError(error: unknown): error is PathError {
  return (
    isRecord(error) &&
    typeof error.message === 'string' &&
    Array.isArray(error.path) &&
    error.path.length > 0 &&
    error.path.every(
      (segment) =>
        (typeof segment === 'string' && segment !== '@') || typeof segment === 'number',
    )
  )
}

function errorKey(error: PathError) {
  return `${error.path.join('.')}:${error.message}`
}

function surfaceInResult<T extends ExecutionResult>(response: T): T {
  if (response.data == null) {
    return response
  }

  const errors = response.errors ?? []
  const valueCompletion = isRecord(response.extensions)
    ? asArray(response.extensions.valueCompletion)
    : []
  const subgraphErrors = errors.flatMap((error) => asArray(error.extensions?.errors))

  const seen = new Set(errors.filter(isPathError).map(errorKey))
  const surfaced = [...valueCompletion, ...subgraphErrors]
    .filter(isPathError)
    .filter((error) => {
      const key = errorKey(error)
      if (seen.has(key)) {
        return false
      }
      seen.add(key)
      return true
    })

  if (surfaced.length === 0) {
    return response
  }

  return { ...response, errors: [...errors, ...surfaced] }
}

function isBatch<T>(response: T | readonly T[]): response is readonly T[] {
  return Array.isArray(response)
}

/**
 * Copies router errors Relay never reads (`extensions.valueCompletion` and
 * subgraph errors nested in a path-less wrapper) into `errors`, so Relay
 * attaches them to the field they nulled.
 */
export function surfaceRouterErrors<T extends ExecutionResult>(response: T): T
export function surfaceRouterErrors<T extends ExecutionResult>(
  response: readonly T[],
): readonly T[]
export function surfaceRouterErrors<T extends ExecutionResult>(
  response: T | readonly T[],
): T | readonly T[]
export function surfaceRouterErrors<T extends ExecutionResult>(
  response: T | readonly T[],
): T | readonly T[] {
  return isBatch(response) ? response.map(surfaceInResult) : surfaceInResult(response)
}
